/**
 * generateLead.ts — the ONE place `generate_lead` (C1) may be pushed, and the
 * only thing that makes TWO acquisition lanes safe to have.
 *
 * ── THE DEFECT THIS EXISTS FOR ──────────────────────────────────────────────
 * `generate_lead` had exactly one call site: `journey/CaptureStep.tsx`, on the
 * DIRECT-ENTRY path (someone who opens `/weightloss` and types their details
 * into Step 0). That is not how patients actually arrive. The canonical
 * acquisition lane is the Webflow widget, which creates the Lead ITSELF
 * (`embed/main.ts` → `createLead`) and then redirects into the portal with the
 * id already minted — production `Deal.entryPath` is widget_form 26 /
 * callback_button 2 / portal_journey 1, so roughly 97% of real leads came down
 * a lane that never renders CaptureStep. The deployed widget bundle carries no
 * tracking code at all (46,097 bytes, zero occurrences of `dataLayer` or
 * `generate_lead`), and `JourneyPage`'s hand-off sets the stage straight to
 * `projection`, so C1 could never fire for them. Everything downstream DID fire
 * — `questionnaire_start` and the rest take the leadId out of the hand-off — so
 * the funnel showed a step-2 population with almost no step 1 above it.
 *
 * ── WHY THE FIX IS HERE AND NOT IN THE WIDGET ───────────────────────────────
 * The widget runs on `booking.thegpservice.co.uk` — a different document, a
 * different container and a Webflow page we do not deploy from this repo. A fix
 * there would be a portal change that cannot ship without a Webflow/GTM change
 * landing in lock-step. So the PORTAL pushes C1 when a hand-off resolves a lead
 * the portal did not create. Self-contained, ships on its own.
 *
 * ── THE CONSTRAINT: TWO DOORS, ONE EVENT ────────────────────────────────────
 * With two lanes able to push C1, the failure mode flips from "no event" to
 * "two events for one lead", which is worse: an undercount is visibly broken,
 * a double-count reads as growth. The guarantee is STRUCTURAL rather than
 * argued from routing — there is no `trackGenerateLead` call anywhere in the
 * app except the one below, PINNED by `callSites.guard.spec.ts` (which names
 * every file that calls it and fails on a second one), and this function is
 * idempotent per leadId. Both lanes call it, so even if a single lead somehow
 * travelled both roads (a hand-off that later restarts into Step 0, a route
 * change nobody predicted) the second call is a no-op.
 *
 * ── WHERE "HAVE WE COUNTED THIS LEAD?" LIVES ────────────────────────────────
 * It is a fact about the LEAD, not about the browser, so it belongs to the
 * service that owns the Lead row. The first cut of this module got that wrong:
 * it kept a 30-day pseudonymous digest of each counted leadId in `localStorage`,
 * written on arrival, before the consent banner had been answered.
 *
 * That is a PECR reg. 6 breach, and pseudonymity does not rescue it — reg. 6
 * bites on the ACT of storing information on (or reading it from) the visitor's
 * device, whatever the value dereferences to. The exemption is "strictly
 * necessary for a service the subscriber or user has requested", and a durable
 * analytics de-dupe marker is not that: the journey works perfectly without it,
 * only the accuracy of OUR reporting suffers. It also contradicted this repo's
 * own written rule — `src/tracking/README.md` says every non-essential store is
 * opt-in, defaulted to `denied` before GTM even loads.
 *
 * So the de-dupe is now split in two, and NEITHER half touches the device:
 *
 *   1. `emittedThisDocument`, a module-level Set — page memory, gone when the
 *      document goes. Covers React StrictMode's dev double-invoke, any effect
 *      re-run, and every re-mount inside one SPA session (the journey remounts
 *      the flow on a hard-stop "Back"; the callback lane replaces back onto the
 *      journey carrying the same lead). Same shape as QuestionFlow's
 *      `questionnaireStartEmittedForAssessment`.
 *
 *   2. THE SERVER, for everything a new document cannot see. clinic-service
 *      (`fix/lead-resume-capability-ttl`) makes `POST /v1/clinic/leads/resume`
 *      single-use: it stamps `Lead.resumeConsumedAt` on the first spend, refuses
 *      the hand-off entirely once a 15-minute TTL or a 2-minute replay grace has
 *      passed, and — with `firstResume` on the response — TELLS the portal which
 *      spend this was. `firstResume: false` means "this hand-off has already
 *      been counted", from the only party in a position to know.
 *
 * A page REFRESH is defended a third way, structurally and deliberately: both
 * hand-off forms are scrubbed out of the address bar on arrival
 * (`journey/leadHandoff.ts`), so a refresh reloads a URL with no lead on it and
 * cannot reach this function at all.
 *
 * ── WHAT IS COVERED, EXACTLY, AND WHAT IS NOT ───────────────────────────────
 * With `firstResume` present on the resume response:
 *
 *   • StrictMode double-invoke ......................... Set
 *   • re-mount in the same document .................... Set
 *   • refresh .......................................... URL scrub, then server
 *   • bfcache Back to Webflow + re-press of the CTA .... server
 *       (the widget caches `sessionLead`, so the re-press re-uses the SAME
 *        leadId rather than minting a new one — a new document, so only the
 *        server can see it)
 *   • the same link re-opened in a second tab / forwarded  server
 *       (inside the 2-minute grace it resolves and says `firstResume: false`;
 *        outside it the resume 404s and nothing is pushed at all)
 *
 * While clinic-service has NOT shipped the field (`'server-silent'`), this
 * degrades to the Set alone. Say plainly what that does and does not cover:
 * StrictMode, effect re-runs and same-document re-mounts are still exact;
 * refresh is still covered by the URL scrub; the bfcache re-press and the
 * re-opened link are NOT — each is a fresh document with a fresh Set, so they
 * push a second C1 for one lead. How wide that gap is depends on what is
 * deployed on the other side: with neither the TTL nor the field (today) a
 * re-opened link double-counts however long afterwards it is opened; with the
 * TTL but not the field, only a re-open inside the 2-minute replay grace does,
 * because a later one is refused outright and this function is only ever reached
 * on a lead that RESOLVED. Firing anyway is the deliberate choice: the
 * alternative is to refuse every hand-off until the server speaks, which ships
 * the empty step 1 this change exists to fix. Once clinic-service #149 carries
 * `firstResume`, that gap closes with no portal change.
 *
 * ── WHY NOT JUST TRUST THE 404? ─────────────────────────────────────────────
 * Single-use alone already shrinks the re-opened-link window from "forever" to
 * "the 2-minute replay grace", because a later re-open is refused outright and
 * this module is only reached on a lead that RESOLVED. The grace exists so a
 * patient whose resume response is lost to a dropped connection is not stranded
 * (see `lead-resume-window.ts` in clinic-service), and it is exactly the window
 * in which a re-open would double-count. One boolean closes it; nothing else
 * available to the portal does.
 */
import { trackGenerateLead } from './dataLayer';

/**
 * How the caller knows this lead has not been counted before. Every value is a
 * claim about the LEAD, sourced from whoever is entitled to make it — nothing
 * here is read off the visitor's device.
 */
export type LeadFirstTouch =
  /**
   * The portal minted this Lead in THIS document — `POST /v1/clinic/leads` has
   * just answered with the id (`journey/CaptureStep.tsx`). A freshly minted id
   * cannot have been counted before; there is no earlier document to have seen
   * it.
   */
  | 'minted-here'
  /** clinic-service resolved the hand-off and said this was the FIRST spend. */
  | 'server-first-resume'
  /**
   * clinic-service resolved the hand-off and said it had ALREADY been spent —
   * a re-press, a second tab, a forwarded link. Never count it.
   */
  | 'server-repeat-resume'
  /**
   * The resume response carried no `firstResume` at all (clinic-service before
   * the field ships). Degrades to in-document de-duping — see the header for
   * the two cases that leaves open.
   */
  | 'server-silent';

/**
 * Read the first-touch verdict off a resume response.
 *
 * `firstResume` is OPTIONAL on the wire on purpose: the portal half of this
 * ships first, and an absent field must mean "the server did not say" rather
 * than silently reading as `false` (which would switch C1 off for the whole
 * widget lane) or as `true` (which would claim a guarantee we do not have).
 */
export function firstTouchOfResume(resume: { firstResume?: boolean }): LeadFirstTouch {
  if (resume.firstResume === true) return 'server-first-resume';
  if (resume.firstResume === false) return 'server-repeat-resume';
  return 'server-silent';
}

/**
 * Leads already counted in THIS document, plus any the server has told us were
 * already spent. A fresh document starts empty — which is the whole reason the
 * authority above it is the server and not a marker on the visitor's disk.
 */
const emittedThisDocument = new Set<string>();

/**
 * Push C1 `generate_lead` for this lead, or do nothing because it has already
 * been counted. THE ONLY WAY THE EVENT IS EMITTED — call it from every lane.
 *
 * `firstTouch` is REQUIRED rather than defaulted: a call site that cannot say
 * how it knows this lead is new is a call site that should not be pushing a
 * conversion, and a default would let the next lane added quietly inherit the
 * weakest guarantee.
 *
 * Returns whether the event was actually pushed, which is what makes the
 * once-only behaviour assertable without reaching into `window.dataLayer`.
 */
export function emitGenerateLeadOnce(leadId: string, firstTouch: LeadFirstTouch): boolean {
  if (!leadId) return false;

  // The server's refusal is recorded, not just obeyed: a later call in the same
  // document (a re-mount, a retry that comes back `server-silent`) must not be
  // able to talk us into counting a lead we have been told is already spent.
  if (firstTouch === 'server-repeat-resume') {
    emittedThisDocument.add(leadId);
    return false;
  }

  // Claim the id BEFORE anything that could early-return, so a synchronous
  // second call (StrictMode) can never reach the push.
  if (emittedThisDocument.has(leadId)) return false;
  emittedThisDocument.add(leadId);

  trackGenerateLead({ leadId });
  return true;
}

/**
 * TEST-ONLY: clear the de-dupe so specs are isolated.
 * WHY: the Set is process-global (correct for StrictMode in the app) but a test
 * process is not a document — without a reset, the second spec using the
 * `lead-1` fixture silently gets no `generate_lead` and asserts nothing. Same
 * trap `__resetQuestionnaireStartTracking` exists for.
 */
export function __resetGenerateLeadTracking(): void {
  emittedThisDocument.clear();
}
